import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  subscribe,
  getDashboardOverview,
  getDataQuality,
  getAnalytics,
  getSecurity,
  getArchitecture,
  getCompliance,
  getKPIs,
  getCriticalAlerts,
  getTrends,
  getRecommendations,
  getFrameworkCompliance,
  getDataQualityDimensions,
  getAnalyticsMaturity,
  refreshData,
  resolveAlert
} from '../../services/data/dataDashboardService';
import { add as addAuditLog } from '../../services/bread/auditLogService';

const Data = () => {
  const [dataState, setDataState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = subscribe(setDataState);
    setLoading(false);
    
    // Log access
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'page_access', 
      target: 'data_dashboard', 
      status: 'success' 
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleRefreshData = () => {
    setLoading(true);
    refreshData();
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'data_refresh', 
      target: 'data_dashboard', 
      status: 'success' 
    });
    setTimeout(() => setLoading(false), 1000);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleResolveAlert = (alertId) => {
    resolveAlert(alertId);
    setSelectedAlert(null);
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'alert_resolved', 
      target: alertId, 
      status: 'success' 
    });
  };

  const navigateToSection = (section) => {
    const routeMap = {
      'analytics-intelligence': '/data/analytics-intelligence',
      'business-intelligence': '/data/business-intelligence'
    };
    
    if (routeMap[section]) {
      navigate(routeMap[section]);
    }
  };

  if (loading || !dataState) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading data dashboard...</span>
          </div>
          <p className="mt-2">Loading Data Department Dashboard...</p>
        </div>
      </div>
    );
  }

  const overview = getDashboardOverview();
  const dataQuality = getDataQuality();
  const analytics = getAnalytics();
  const security = getSecurity();
  const architecture = getArchitecture();
  const compliance = getCompliance();
  const kpis = getKPIs();
  const criticalAlerts = getCriticalAlerts();
  const trends = getTrends();
  const recommendations = getRecommendations();
  const frameworks = getFrameworkCompliance();
  const qualityDimensions = getDataQualityDimensions();
  const analyticsMaturity = getAnalyticsMaturity();

  const OverviewTab = () => (
    <div className="row">
      {/* Executive Summary Cards */}
      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100 bg-primary text-white">
          <div className="card-body text-center">
            <div className="h2 mb-2">{overview.totalDataAssets.toLocaleString()}</div>
            <div className="mb-2">Total Data Assets</div>
            <small>{overview.dataVolume} total volume</small>
          </div>
        </div>
      </div>

      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100 bg-success text-white">
          <div className="card-body text-center">
            <div className="h2 mb-2">{overview.dataQualityScore}%</div>
            <div className="mb-2">Data Quality Score</div>
            <small>6 dimensions tracked</small>
          </div>
        </div>
      </div>

      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100 bg-info text-white">
          <div className="card-body text-center">
            <div className="h2 mb-2">{overview.systemAvailability}%</div>
            <div className="mb-2">System Availability</div>
            <small>{overview.dataLatency} avg latency</small>
          </div>
        </div>
      </div>

      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100 bg-warning text-dark">
          <div className="card-body text-center">
            <div className="h2 mb-2">{overview.analyticsMaturityScore}/5</div>
            <div className="mb-2">Analytics Maturity</div>
            <small>{overview.activeDataSources} data sources</small>
          </div>
        </div>
      </div>

      {/* Critical Alerts */}
      {criticalAlerts.length > 0 && (
        <div className="col-12 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-danger text-white">
              <h5 className="mb-0">
                <i className="fas fa-exclamation-triangle me-2"></i>Critical Data Alerts
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                {criticalAlerts.map((alert, index) => (
                  <div key={index} className="col-md-4 mb-3">
                    <div className="card border-danger">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">{alert.type}</h6>
                          <span className="badge bg-danger">{alert.severity}</span>
                        </div>
                        <p className="small mb-2">{alert.message}</p>
                        <div className="mb-2">
                          <small><strong>Source:</strong> {alert.source}</small><br/>
                          <small><strong>Impact:</strong> {alert.impact}</small>
                        </div>
                        <div className="mb-2">
                          <small><strong>Action:</strong> {alert.action}</small>
                        </div>
                        <div className="d-flex justify-content-between">
                          <small><strong>Assigned:</strong> {alert.assignedTo}</small>
                          <button 
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setSelectedAlert(alert)}
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPI Dashboard */}
      <div className="col-md-8 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-tachometer-alt me-2"></i>Key Performance Indicators
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              {Object.entries(kpis).map(([key, kpi]) => (
                <div key={key} className="col-md-6 mb-4">
                  <div className="card border-0 bg-light h-100">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <h6 className="mb-0">{kpi.description}</h6>
                          <small className="text-muted">Target: {kpi.target}</small>
                        </div>
                        <div className="text-center">
                          <div className={`h4 mb-0 ${
                            kpi.trend === 'Improving' ? 'text-success' : 
                            kpi.trend === 'Declining' ? 'text-danger' : 'text-warning'
                          }`}>
                            {kpi.current}
                          </div>
                          <small className={`text-${
                            kpi.trend === 'Improving' ? 'success' : 
                            kpi.trend === 'Declining' ? 'danger' : 'warning'
                          }`}>
                            <i className={`fas fa-arrow-${
                              kpi.trend === 'Improving' ? 'up' : 
                              kpi.trend === 'Declining' ? 'down' : 'right'
                            } me-1`}></i>
                            {kpi.trend}
                          </small>
                        </div>
                      </div>
                      <div className="progress" style={{ height: '6px' }}>
                        <div 
                          className={`progress-bar bg-${
                            kpi.current >= kpi.target ? 'success' : 
                            kpi.current >= kpi.target * 0.8 ? 'warning' : 'danger'
                          }`}
                          style={{ width: `${Math.min(100, (kpi.current / kpi.target) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Data Architecture Health */}
      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-info text-white">
            <h5 className="mb-0">
              <i className="fas fa-server me-2"></i>Architecture Health
            </h5>
          </div>
          <div className="card-body">
            {Object.entries(architecture).map(([key, component]) => (
              <div key={key} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">{component.name}</h6>
                  <span className={`badge bg-${
                    component.health === 'Excellent' ? 'success' : 
                    component.health === 'Good' ? 'warning' : 'danger'
                  }`}>
                    {component.health}
                  </span>
                </div>
                <div className="small">
                  {component.storage && <div><strong>Storage:</strong> {component.storage}</div>}
                  {component.throughput && <div><strong>Throughput:</strong> {component.throughput}</div>}
                  {component.utilizationRate && <div><strong>Utilization:</strong> {component.utilizationRate}%</div>}
                  {component.availability && <div><strong>Availability:</strong> {component.availability}%</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trends Analysis */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-chart-line me-2"></i>Data Trends
            </h5>
          </div>
          <div className="card-body">
            {Object.entries(trends).map(([key, trend]) => (
              <div key={key} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">{trend.name}</h6>
                  <span className={`badge bg-${
                    trend.trend === 'Increasing' || trend.trend === 'Improving' ? 'success' : 
                    trend.trend === 'Decreasing' || trend.trend === 'Declining' ? 'danger' : 'warning'
                  }`}>
                    {trend.trend}
                  </span>
                </div>
                <div className="small text-muted">
                  {trend.current && <div><strong>Current:</strong> {trend.current}</div>}
                  {trend.monthlyGrowth && <div><strong>Monthly Growth:</strong> {trend.monthlyGrowth}%</div>}
                  {trend.target && <div><strong>Target:</strong> {trend.target}</div>}
                  {trend.impact && <div><strong>Impact:</strong> {trend.impact}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-warning text-dark">
            <h5 className="mb-0">
              <i className="fas fa-lightbulb me-2"></i>Strategic Recommendations
            </h5>
          </div>
          <div className="card-body">
            {recommendations.slice(0, 4).map((rec, index) => (
              <div key={index} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">{rec.title}</h6>
                  <span className={`badge bg-${
                    rec.priority === 'High' ? 'danger' : 
                    rec.priority === 'Medium' ? 'warning' : 'success'
                  }`}>
                    {rec.priority}
                  </span>
                </div>
                <p className="small mb-2">{rec.description}</p>
                <div className="small text-muted">
                  <div><strong>Expected Impact:</strong> {rec.expectedImpact}</div>
                  <div><strong>Timeline:</strong> {rec.timeline} | <strong>Effort:</strong> {rec.effort}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Department Quick Access */}
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-sitemap me-2"></i>Data Department Modules
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <div className="card border-primary h-100">
                  <div className="card-body text-center">
                    <i className="fas fa-chart-bar fa-3x text-primary mb-3"></i>
                    <h6 className="card-title">Analytics & Intelligence</h6>
                    <p className="card-text small">Raw intelligence, predictive analytics, and custom reports</p>
                    <button 
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => navigateToSection('analytics-intelligence')}
                    >
                      <i className="fas fa-arrow-right me-1"></i>Access Module
                    </button>
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div className="card border-info h-100">
                  <div className="card-body text-center">
                    <i className="fas fa-chart-line fa-3x text-info mb-3"></i>
                    <h6 className="card-title">Business Intelligence</h6>
                    <p className="card-text small">Market analysis, investor dashboard, and performance metrics</p>
                    <button 
                      className="btn btn-outline-info btn-sm"
                      onClick={() => navigateToSection('business-intelligence')}
                    >
                      <i className="fas fa-arrow-right me-1"></i>Access Module
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const QualityTab = () => (
    <div className="row">
      {/* Data Quality Overview */}
      <div className="col-md-8 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-check-circle me-2"></i>Data Quality Dimensions (ISO 8000 Standard)
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="text-success">{dataQuality.overallScore}%</h4>
                  <p className="text-muted">Overall Data Quality Score</p>
                </div>
                <div className="text-end">
                  <span className={`badge bg-${dataQuality.trend === 'Improving' ? 'success' : 'warning'}`}>
                    {dataQuality.trend}
                  </span>
                </div>
              </div>
            </div>

            <div className="row">
              {Object.entries(qualityDimensions).map(([key, dimension]) => (
                <div key={key} className="col-md-6 mb-3">
                  <div className="card border-0 bg-light">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">{dimension.name}</h6>
                        <span className={`badge bg-${
                          dimension.score >= dimension.target ? 'success' : 
                          dimension.score >= dimension.target * 0.9 ? 'warning' : 'danger'
                        }`}>
                          {dimension.score}%
                        </span>
                      </div>
                      <div className="mb-2">
                        <div className="d-flex justify-content-between mb-1">
                          <small>Target: {dimension.target}%</small>
                          <small className={`text-${
                            dimension.trend === 'Improving' ? 'success' : 
                            dimension.trend === 'Declining' ? 'danger' : 'warning'
                          }`}>
                            <i className={`fas fa-arrow-${
                              dimension.trend === 'Improving' ? 'up' : 
                              dimension.trend === 'Declining' ? 'down' : 'right'
                            } me-1`}></i>
                            {dimension.trend}
                          </small>
                        </div>
                        <div className="progress" style={{ height: '6px' }}>
                          <div 
                            className={`progress-bar bg-${
                              dimension.score >= dimension.target ? 'success' : 
                              dimension.score >= dimension.target * 0.9 ? 'warning' : 'danger'
                            }`}
                            style={{ width: `${dimension.score}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quality Issues */}
      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-warning text-dark">
            <h5 className="mb-0">
              <i className="fas fa-exclamation-circle me-2"></i>Quality Issues
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold">Critical</span>
                <span className="badge bg-danger">{dataQuality.issues.critical}</span>
              </div>
              <small className="text-muted">Avg Resolution: {dataQuality.resolutionTime.critical}</small>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold">High</span>
                <span className="badge bg-warning text-dark">{dataQuality.issues.high}</span>
              </div>
              <small className="text-muted">Avg Resolution: {dataQuality.resolutionTime.high}</small>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold">Medium</span>
                <span className="badge bg-info">{dataQuality.issues.medium}</span>
              </div>
              <small className="text-muted">Avg Resolution: {dataQuality.resolutionTime.medium}</small>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold">Low</span>
                <span className="badge bg-secondary">{dataQuality.issues.low}</span>
              </div>
              <small className="text-muted">Avg Resolution: {dataQuality.resolutionTime.low}</small>
            </div>

            <hr />
            <div className="text-center">
              <h5 className="text-primary">{dataQuality.issues.total}</h5>
              <small className="text-muted">Total Open Issues</small>
              <div className="mt-2">
                <small className="text-success">{dataQuality.automatedChecks}% Automated Checks</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const AnalyticsTab = () => (
    <div className="row">
      {/* Analytics Maturity */}
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-chart-pie me-2"></i>Analytics Maturity Assessment (Gartner Model)
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="text-info">{analytics.overallMaturity}/5</h4>
                  <p className="text-muted">Overall Analytics Maturity</p>
                </div>
                <div className="text-end">
                  <div><strong>Business Value:</strong> {analytics.businessValue}</div>
                  <div><strong>User Adoption:</strong> {analytics.userAdoption}%</div>
                </div>
              </div>
            </div>

            <div className="row">
              {Object.entries(analyticsMaturity).map(([key, level]) => (
                <div key={key} className="col-md-6 mb-4">
                  <div className="card border-0 bg-light h-100">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <h6 className="mb-0">{level.name}</h6>
                          <small className="text-muted">{level.description}</small>
                        </div>
                        <div className="text-center">
                          <div className="h5 mb-0 text-primary">{level.maturityLevel}/5</div>
                          <small className="text-muted">Maturity</small>
                        </div>
                      </div>
                      
                      <div className="mb-2">
                        <small><strong>Usage:</strong> {level.usage}%</small>
                        <div className="progress mt-1" style={{ height: '4px' }}>
                          <div 
                            className="progress-bar bg-info" 
                            style={{ width: `${level.usage}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="mb-2">
                        <small><strong>ROI:</strong> {level.roi}%</small>
                      </div>

                      <div>
                        <small className="fw-bold text-muted">Key Capabilities:</small>
                        {level.capabilities.slice(0, 3).map((capability, idx) => (
                          <div key={idx} className="small text-muted">• {capability}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="row mt-3">
              <div className="col-md-3">
                <div className="text-center">
                  <h5 className="text-primary">{analytics.activeModels}</h5>
                  <small className="text-muted">Active Models</small>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center">
                  <h5 className="text-success">{analytics.modelAccuracy}%</h5>
                  <small className="text-muted">Model Accuracy</small>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center">
                  <h5 className="text-info">{analytics.deployedInsights}</h5>
                  <small className="text-muted">Deployed Insights</small>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center">
                  <h5 className="text-warning">{analytics.businessValue}</h5>
                  <small className="text-muted">Quarterly Value</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ComplianceTab = () => (
    <div className="row">
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-shield-alt me-2"></i>Data Framework Compliance
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              {Object.entries(frameworks).map(([key, framework]) => (
                <div key={key} className="col-md-6 mb-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">{framework.name}</h6>
                      <span className={`badge bg-${
                        framework.status === 'Certified' || framework.status === 'Compliant' ? 'success' : 
                        framework.status === 'Advanced' || framework.status === 'Mature' ? 'info' : 'warning'
                      }`}>
                        {framework.status}
                      </span>
                    </div>
                    <div className="card-body">
                      <p className="small text-muted mb-3">{framework.description}</p>
                      
                      <div className="mb-3">
                        {framework.complianceScore && (
                          <>
                            <div className="d-flex justify-content-between mb-1">
                              <small><strong>Compliance Score</strong></small>
                              <small>{framework.complianceScore}%</small>
                            </div>
                            <div className="progress mb-2" style={{ height: '8px' }}>
                              <div 
                                className={`progress-bar bg-${
                                  framework.complianceScore >= 90 ? 'success' : 
                                  framework.complianceScore >= 75 ? 'warning' : 'danger'
                                }`}
                                style={{ width: `${framework.complianceScore}%` }}
                              ></div>
                            </div>
                          </>
                        )}
                        {framework.maturityScore && (
                          <>
                            <div className="d-flex justify-content-between mb-1">
                              <small><strong>Maturity Score</strong></small>
                              <small>{framework.maturityScore}%</small>
                            </div>
                            <div className="progress mb-2" style={{ height: '8px' }}>
                              <div 
                                className="progress-bar bg-info" 
                                style={{ width: `${framework.maturityScore}%` }}
                              ></div>
                            </div>
                          </>
                        )}
                        {framework.maturityLevel && (
                          <div className="mb-2">
                            <small><strong>Maturity Level:</strong> {framework.maturityLevel}/5</small>
                          </div>
                        )}
                      </div>

                      <div className="mb-3">
                        <small className="fw-bold text-muted">Key Areas:</small>
                        {(framework.domains || framework.controls || framework.functions || framework.principles)?.slice(0, 4).map((item, idx) => (
                          <div key={idx} className="small text-muted">• {item}</div>
                        ))}
                      </div>

                      <div className="d-flex justify-content-between">
                        <small><strong>Last Review:</strong> {new Date(framework.lastAssessment || framework.lastAudit || framework.lastReview).toLocaleDateString()}</small>
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setSelectedFramework(framework)}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Audit Summary */}
            <div className="row mt-4">
              <div className="col-md-6">
                <div className="card border-info">
                  <div className="card-header bg-info text-white">
                    <h6 className="mb-0">Audit Summary</h6>
                  </div>
                  <div className="card-body">
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <span>Completed Audits</span>
                        <span className="badge bg-success">{compliance.audits.completed}</span>
                      </div>
                    </div>
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <span>Scheduled Audits</span>
                        <span className="badge bg-info">{compliance.audits.scheduled}</span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>Remediation Rate</span>
                        <span className="badge bg-warning text-dark">{compliance.audits.remediation}%</span>
                      </div>
                    </div>
                    <div className="border-top pt-2">
                      <small><strong>Findings:</strong> {compliance.audits.findings.high} High, {compliance.audits.findings.medium} Medium, {compliance.audits.findings.low} Low</small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card border-success">
                  <div className="card-header bg-success text-white">
                    <h6 className="mb-0">Privacy Compliance</h6>
                  </div>
                  <div className="card-body">
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <span>GDPR Readiness</span>
                        <span className="badge bg-success">{compliance.privacy.gdprReadiness}%</span>
                      </div>
                    </div>
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <span>CCPA Compliance</span>
                        <span className="badge bg-success">{compliance.privacy.ccpaCompliance}%</span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>HIPAA Compliance</span>
                        <span className="badge bg-success">{compliance.privacy.hipaaCompliance}%</span>
                      </div>
                    </div>
                    <div className="border-top pt-2">
                      <small><strong>Data Requests:</strong> {compliance.privacy.dataRequests} received, {compliance.privacy.requestFulfillment}% fulfilled</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-0">Data Department</h2>
              <p className="text-muted">Enterprise data management and analytics platform</p>
            </div>
            <button 
              className="btn btn-outline-primary"
              onClick={handleRefreshData}
              disabled={loading}
            >
              <i className="fas fa-sync-alt me-2"></i>Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-pills">
            {[
              { id: 'overview', label: 'Dashboard Overview', icon: 'fas fa-tachometer-alt' },
              { id: 'quality', label: 'Data Quality', icon: 'fas fa-check-circle' },
              { id: 'analytics', label: 'Analytics Maturity', icon: 'fas fa-chart-pie' },
              { id: 'compliance', label: 'Framework Compliance', icon: 'fas fa-shield-alt' }
            ].map(tab => (
              <li key={tab.id} className="nav-item">
                <button
                  className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  <i className={`${tab.icon} me-2`}></i>{tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'quality' && <QualityTab />}
        {activeTab === 'analytics' && <AnalyticsTab />}
        {activeTab === 'compliance' && <ComplianceTab />}
      </div>

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {selectedAlert.type} Alert
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setSelectedAlert(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>Alert Details</h6>
                    <div className="mb-2">
                      <strong>Severity:</strong> 
                      <span className={`badge bg-${selectedAlert.severity === 'High' ? 'danger' : 'warning'} ms-2`}>
                        {selectedAlert.severity}
                      </span>
                    </div>
                    <div className="mb-2">
                      <strong>Source:</strong> {selectedAlert.source}
                    </div>
                    <div className="mb-2">
                      <strong>Impact:</strong> {selectedAlert.impact}
                    </div>
                    <div className="mb-2">
                      <strong>Created:</strong> {new Date(selectedAlert.created).toLocaleString()}
                    </div>
                    <div className="mb-2">
                      <strong>Due Date:</strong> {new Date(selectedAlert.dueDate).toLocaleString()}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6>Message</h6>
                    <p>{selectedAlert.message}</p>
                    <h6>Recommended Action</h6>
                    <p>{selectedAlert.action}</p>
                    <div className="mb-2">
                      <strong>Assigned To:</strong> {selectedAlert.assignedTo}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setSelectedAlert(null)}
                >
                  Close
                </button>
                <button 
                  type="button" 
                  className="btn btn-success"
                  onClick={() => handleResolveAlert(selectedAlert.id)}
                >
                  Mark as Resolved
                </button>
                <button type="button" className="btn btn-primary">
                  Assign to Team
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Framework Detail Modal */}
      {selectedFramework && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-shield-alt me-2"></i>
                  {selectedFramework.name}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setSelectedFramework(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>Framework Overview</h6>
                    <p className="text-muted">{selectedFramework.description}</p>
                    <div className="mb-3">
                      <strong>Status:</strong> 
                      <span className={`badge bg-${
                        selectedFramework.status === 'Certified' || selectedFramework.status === 'Compliant' ? 'success' : 'info'
                      } ms-2`}>
                        {selectedFramework.status}
                      </span>
                    </div>
                    {selectedFramework.complianceScore && (
                      <div className="mb-3">
                        <strong>Compliance Score:</strong> {selectedFramework.complianceScore}%
                      </div>
                    )}
                    {selectedFramework.maturityScore && (
                      <div className="mb-3">
                        <strong>Maturity Score:</strong> {selectedFramework.maturityScore}%
                      </div>
                    )}
                    {selectedFramework.maturityLevel && (
                      <div className="mb-3">
                        <strong>Maturity Level:</strong> {selectedFramework.maturityLevel}/5
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <h6>Key Components</h6>
                    {(selectedFramework.domains || selectedFramework.controls || selectedFramework.functions || selectedFramework.principles)?.map((item, index) => (
                      <div key={index} className="mb-2 p-2 border rounded">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold">{item}</span>
                          <span className="badge bg-success">Compliant</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setSelectedFramework(null)}
                >
                  Close
                </button>
                <button type="button" className="btn btn-primary">
                  Generate Assessment Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Data;